import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Calendar, Check, Loader2, Pencil, Plus, Trash2, X,
} from "lucide-react";

import { IndicatorFormDialog } from "@/components/IndicatorFormDialog";
import { IndicatorTrendChart } from "@/components/IndicatorTrendChart";
import { ImportCsvDialog } from "@/components/ImportCsvDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useCreateIndicatorValue, useDeleteIndicator, useDeleteIndicatorValue,
  useIndicator, useIndicatorValues,
} from "@/hooks/useIndicators";
import { formatDateTime } from "@/lib/format";
import {
  formatPeriod, quarterFromIso, quarterStartIso, todayForPeriod,
} from "@/lib/indicator-period";
import { cn } from "@/lib/utils";
import type { IndicatorPeriod, IndicatorValueRow } from "@/lib/types";

import { DirectionChip, VisibilityBadge, prettyValue } from "./Indicators";
import { NotFound } from "./DocumentDetail";

/** /indicators/:id — full detail incl. all observations + add-value form. */
export default function IndicatorDetail() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id;

  const { data: user } = useCurrentUser();
  const detail = useIndicator(id);
  const values = useIndicatorValues(id);
  const del    = useDeleteIndicator();
  const confirm = useConfirm();

  const [editing,    setEditing]    = useState(false);
  const [importing,  setImporting]  = useState(false);

  const canManage = !!user?.is_ceo_level;

  if (detail.isLoading) return <DetailSkeleton />;
  if (detail.error || !detail.data) return <NotFound back="/indicators" label="indicators" />;

  const ind = detail.data;

  async function onDelete() {
    const ok = await confirm({
      title: `Delete "${ind.name}"?`,
      description: "Every observation goes with it. This cannot be undone.",
      confirmLabel: "Delete indicator",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(ind.id, { onSuccess: () => navigate("/indicators") });
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/indicators">
            <ArrowLeft className="mr-2 h-4 w-4" />Back to indicators
          </Link>
        </Button>
        <div className="flex gap-2">
          {/* CSV export is available to everyone who can see the
              indicator — sharing values out for board packs etc. */}
          <Button asChild variant="outline" size="sm">
            <a href={`/api/v1/indicators/${ind.id}/values.csv`} download>
              Export CSV
            </a>
          </Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={() => setImporting(true)}>
                Import CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />Edit
              </Button>
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onDelete} disabled={del.isPending}
              >
                {del.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Trash2 className="mr-2 h-4 w-4" />}
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex items-start gap-5 p-6 md:p-7">
          <DirectionChip direction={ind.direction} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <VisibilityBadge state={ind.workflow_state} label={ind.workflow_state_display} />
              <Badge variant="outline" className="font-medium">
                {ind.direction_display}{ind.unit ? ` · ${ind.unit}` : ""}
              </Badge>
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary font-medium">
                {ind.period_display}
              </Badge>
              {ind.category_names.map((name) => (
                <Badge key={name} variant="outline" className="font-medium">
                  {name}
                </Badge>
              ))}
            </div>
            <h1 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">{ind.name}</h1>
            {ind.description && (
              <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                {ind.description}
              </p>
            )}
            {/* At-a-glance: latest value vs target. */}
            <div className="mt-5 flex flex-wrap items-end gap-x-8 gap-y-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Latest</div>
                <div className="mt-0.5 flex items-baseline gap-1.5">
                  <span className="font-mono text-3xl font-bold tabular-nums tracking-tight">
                    {ind.latest_value ? prettyValue(ind.latest_value) : "—"}
                  </span>
                  {ind.unit && <span className="text-sm text-muted-foreground">{ind.unit}</span>}
                </div>
                {ind.latest_date && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {formatPeriod(ind.latest_date, ind.period)}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Observations</div>
                <div className="mt-0.5 font-mono text-3xl font-bold tabular-nums">
                  {ind.value_count}
                </div>
              </div>
              <div className="ml-auto text-xs text-muted-foreground">
                Owned by <span className="font-medium text-foreground">{ind.owner_name || "—"}</span>
                {" · "}<Calendar className="mr-0.5 inline h-3 w-3" />Updated {formatDateTime(ind.updated_at)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trend chart — visible to everyone who can see the indicator. */}
      <IndicatorTrendChart
        values={values.data?.results ?? []}
        period={ind.period}
        direction={ind.direction}
        unit={ind.unit}
        loading={values.isLoading}
      />

      {/* Add value (CEO+ only) */}
      {canManage && (
        <AddValueCard
          indicatorId={ind.id}
          latestDate={ind.latest_date}
          unit={ind.unit}
          period={ind.period}
        />
      )}

      {/* Values list */}
      <ValuesTable
        loading={values.isLoading}
        values={values.data?.results ?? []}
        unit={ind.unit}
        period={ind.period}
        canManage={canManage}
      />

      <IndicatorFormDialog open={editing} onOpenChange={setEditing} indicator={ind} />

      <ImportCsvDialog
        open={importing}
        onOpenChange={setImporting}
        title={`Import values for "${ind.name}"`}
        description="Bulk-load historical observations. The file's `date` column is auto-snapped to this indicator's period start — submitting any day inside Q2 writes to the canonical Q2 row."
        endpoint={`/indicators/${ind.id}/values/import/`}
        invalidateKeys={[
          ["indicator-values", ind.id],
          ["indicators", "detail", ind.id],
          ["indicators"],
        ]}
        columnsHint={
          <>
            <div className="font-semibold mb-1">Recognised columns</div>
            <code className="block font-mono text-[11px]">date, value, target, note</code>
            <ul className="mt-2 list-disc pl-4 text-muted-foreground">
              <li><strong>date</strong> + <strong>value</strong> are required; <strong>target</strong> and <strong>note</strong> are optional.</li>
              <li><strong>date</strong> accepts ISO (2026-03-31), DD/MM/YYYY, or MM/DD/YYYY.</li>
              <li>Re-importing the same date <strong>overwrites</strong> that observation.</li>
              <li>This is the exact column shape the "Export CSV" button on this page emits.</li>
            </ul>
          </>
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-value card (CEO+ only)
// ---------------------------------------------------------------------------
function AddValueCard({
  indicatorId, latestDate, unit, period,
}: {
  indicatorId: string;
  latestDate: string | null;
  unit: string;
  period: import("@/lib/types").IndicatorPeriod;
}) {
  const create = useCreateIndicatorValue();
  // Default to "this period" so the common "log the current quarter / month
  // / year" flow is one click. User can change it for backfills.
  const today = useMemo(() => todayForPeriod(period), [period]);
  const [date,   setDate]   = useState(today);
  const [value,  setValue]  = useState("");
  const [target, setTarget] = useState("");
  const [note,   setNote]   = useState("");

  // If the parent indicator's period changes (rare, via the edit dialog),
  // reset to "this period" of the new cadence.
  useEffect(() => { setDate(todayForPeriod(period)); }, [period]);

  const canSubmit = value.trim().length > 0 && date && !create.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        indicator: indicatorId,
        date,
        value,
        target: target.trim() || null,
        note:   note.trim() || undefined,
      },
      {
        onSuccess: () => {
          setValue(""); setTarget(""); setNote("");
        },
      },
    );
  }

  const err = create.error as { response?: { data?: { detail?: string; non_field_errors?: string[] } } } | undefined;
  const errMsg = err?.response?.data?.detail
    || err?.response?.data?.non_field_errors?.[0]
    || null;

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="border-b border-border/60 py-4">
        <CardTitle className="text-base">Add a value</CardTitle>
        {latestDate && (
          <p className="text-xs text-muted-foreground">
            Last observation was <span className="font-medium text-foreground">{formatPeriod(latestDate, period)}</span>.
            Submitting the same period overwrites that row.
          </p>
        )}
      </CardHeader>
      <CardContent className="p-5">
        {/* Wider Period column + bigger gap so the Q1–Q4 pills don't
            crowd the Value input next to them. */}
        <form onSubmit={onSubmit} className="grid gap-x-6 gap-y-3 sm:grid-cols-[minmax(240px,280px)_1fr_1fr_auto]">
          <div className="space-y-1">
            <Label>Period</Label>
            <PeriodPicker period={period} value={date} onChange={setDate} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="iv-value">
              Value <span className="text-destructive">*</span>{unit && <span className="ml-1 text-muted-foreground">({unit})</span>}
            </Label>
            <Input
              id="iv-value" type="number" required step="any" inputMode="decimal"
              value={value} onChange={(e) => setValue(e.target.value)}
              className="h-9 font-mono tabular-nums"
              placeholder="0"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="iv-target">
              Target <span className="ml-1 text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="iv-target" type="number" step="any" inputMode="decimal"
              value={target} onChange={(e) => setTarget(e.target.value)}
              className="h-9 font-mono tabular-nums"
              placeholder="—"
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={!canSubmit} className="h-9 w-full sm:w-auto">
              {create.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : <><Plus className="mr-2 h-4 w-4" />Add</>}
            </Button>
          </div>
          <div className="sm:col-span-4">
            <Textarea
              rows={2}
              placeholder="Optional note for this observation."
              value={note} onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {errMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive sm:col-span-4">
              {errMsg}
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Values table
// ---------------------------------------------------------------------------
function ValuesTable({
  loading, values, unit, canManage, period,
}: {
  loading: boolean;
  values: IndicatorValueRow[];
  unit: string;
  canManage: boolean;
  period: import("@/lib/types").IndicatorPeriod;
}) {
  const del = useDeleteIndicatorValue();
  const confirm = useConfirm();

  async function onRemove(v: IndicatorValueRow) {
    const ok = await confirm({
      title: `Remove the ${formatPeriod(v.date, period)} observation?`,
      description: "The value will be deleted. This cannot be undone.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    del.mutate({ id: v.id, indicator: v.indicator });
  }

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="border-b border-border/60 py-4">
        <CardTitle className="text-base">Observations</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">Period</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Target</TableHead>
              <TableHead className="text-right">Progress</TableHead>
              <TableHead className="hidden md:table-cell">Note</TableHead>
              <TableHead className="hidden lg:table-cell">Recorded by</TableHead>
              {canManage && <TableHead className="w-12 text-right pr-3"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={canManage ? 7 : 6} className="py-3">
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : values.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 7 : 6} className="py-12 text-center text-sm text-muted-foreground">
                  No observations yet. Use the form above to log the first one.
                </TableCell>
              </TableRow>
            ) : (
              values.map((v) => {
                const pct = computeProgress(v.value, v.target);
                return (
                  <TableRow key={v.id} className="border-border/60">
                    <TableCell className="pl-5 py-3 text-sm font-medium">{formatPeriod(v.date, period)}</TableCell>
                    <TableCell className="text-right py-3 font-mono tabular-nums text-sm">
                      {prettyValue(v.value)}{unit && <span className="ml-1 text-muted-foreground text-xs">{unit}</span>}
                    </TableCell>
                    <TableCell className="text-right py-3 font-mono tabular-nums text-sm text-muted-foreground">
                      {v.target != null ? prettyValue(v.target) : "—"}
                    </TableCell>
                    <TableCell className="text-right py-3">
                      {pct != null ? (
                        <span className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
                          pct >= 100
                            ? "border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300"
                            : pct >= 75
                              ? "border-amber-300/60 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300"
                              : "border-rose-300/60 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300",
                        )}>
                          {pct >= 100 ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          {pct}%
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell py-3 text-sm text-muted-foreground">
                      <span className="line-clamp-2">{v.note || "—"}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell py-3 text-xs text-muted-foreground">
                      {v.recorded_by_name || "—"}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right pr-3">
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={del.isPending}
                          onClick={() => onRemove(v)}
                          aria-label="Remove observation"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function computeProgress(value: string, target: string | null): number | null {
  if (!target) return null;
  const v = Number(value), t = Number(target);
  if (!isFinite(v) || !isFinite(t) || t === 0) return null;
  return Math.round((v / t) * 100);
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PeriodPicker — renders the right input shape for each cadence.
//
//   annual    → year dropdown (current ± 5)
//   quarterly → year dropdown + Q1/Q2/Q3/Q4 segmented
//   monthly   → <input type="month">  (browser-native YYYY-MM picker)
//   daily     → <input type="date">
//
// `value` is always a YYYY-MM-DD on the period's first day; `onChange`
// fires with the snapped ISO date. The backend additionally snaps on
// save, so any off-by-one rendering is harmless.
// ---------------------------------------------------------------------------
function PeriodPicker({
  period, value, onChange,
}: {
  period: IndicatorPeriod;
  value: string;
  onChange: (next: string) => void;
}) {
  // No more fixed dropdown range — the year field is a plain number
  // input so the user can type ANY year (1900..2200), which covers
  // backfills going decades back and projections decades forward.
  const MIN_YEAR = 1900;
  const MAX_YEAR = 2200;

  if (period === "annual") {
    const y = value ? new Date(value + "T00:00:00").getFullYear() : new Date().getFullYear();
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">FY</span>
        <Input
          type="number"
          inputMode="numeric"
          min={MIN_YEAR} max={MAX_YEAR}
          value={y}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) return;
            onChange(`${String(next).padStart(4, "0")}-01-01`);
          }}
          className="h-9 w-24 font-mono tabular-nums"
        />
      </div>
    );
  }

  if (period === "quarterly") {
    const { year, quarter } = value
      ? quarterFromIso(value)
      : { year: new Date().getFullYear(), quarter: Math.floor(new Date().getMonth() / 3) + 1 };
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          min={MIN_YEAR} max={MAX_YEAR}
          value={year}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) return;
            onChange(quarterStartIso(next, quarter));
          }}
          className="h-9 w-24 font-mono tabular-nums"
        />
        <div className="inline-flex shrink-0 rounded-md border border-input bg-background p-0.5">
          {[1, 2, 3, 4].map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onChange(quarterStartIso(year, q))}
              className={cn(
                "rounded px-2.5 py-0.5 text-xs font-semibold transition",
                q === quarter
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent",
              )}
            >
              Q{q}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (period === "monthly") {
    // <input type=month> wants YYYY-MM; we store YYYY-MM-01.
    const ym = value ? value.slice(0, 7) : todayForPeriod("monthly").slice(0, 7);
    return (
      <Input
        type="month"
        value={ym}
        onChange={(e) => onChange(`${e.target.value || ym}-01`)}
        className="h-9"
      />
    );
  }

  // daily
  return (
    <Input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      max="2999-12-31"
      className="h-9"
    />
  );
}
